import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { CurrentUserProfile } from '../services/UserService';
import type { User } from '../types/user';
import type { AuthContextValue } from '../types/authContext';
import { AuthContext } from './authContext';
import { authQueryKeys } from './authQueryKeys';
import { getKeycloak, initKeycloak } from './keycloak';
import { mapKeycloakUser } from './mapKeycloakUser';
import { useCurrentUserProfileQuery } from './queries/useCurrentUserProfileQuery';

function mergeKeycloakUserWithProfile(
  keycloakUser: User,
  profile: CurrentUserProfile,
): User {
  const fullName = [profile.firstName, profile.lastName]
    .filter((part): part is string => Boolean(part?.trim()))
    .join(' ');

  return {
    ...keycloakUser,
    id: profile.id ?? keycloakUser.id,
    name: fullName || profile.username || profile.email || keycloakUser.name,
    email: profile.email ?? keycloakUser.email,
  };
}

function KeycloakAuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [isKeycloakReady, setIsKeycloakReady] = useState(
    () => window.mockKeycloak != null,
  );
  const [keycloakUser, setKeycloakUser] = useState<User | null>(() => {
    if (!window.mockKeycloak?.authenticated) {
      return null;
    }

    try {
      return mapKeycloakUser(window.mockKeycloak);
    } catch {
      return null;
    }
  });

  const syncKeycloakUser = useCallback(() => {
    const keycloak = getKeycloak();

    if (!keycloak.authenticated) {
      setKeycloakUser(null);
      queryClient.removeQueries({ queryKey: authQueryKeys.all });
      return;
    }

    setKeycloakUser(mapKeycloakUser(keycloak));
  }, [queryClient]);

  const profileQuery = useCurrentUserProfileQuery(
    isKeycloakReady && keycloakUser !== null,
  );

  const user = useMemo(() => {
    if (!keycloakUser) {
      return null;
    }

    if (profileQuery.data) {
      return mergeKeycloakUserWithProfile(keycloakUser, profileQuery.data);
    }

    return keycloakUser;
  }, [keycloakUser, profileQuery.data]);

  const isLoading =
    !isKeycloakReady ||
    (keycloakUser !== null &&
      profileQuery.isLoading &&
      profileQuery.fetchStatus !== 'idle');

  useEffect(() => {
    let cancelled = false;
    let keycloak: ReturnType<typeof getKeycloak> | null = null;

    void (async () => {
      try {
        await initKeycloak();

        if (cancelled) {
          return;
        }

        keycloak = getKeycloak();
        syncKeycloakUser();

        keycloak.onAuthSuccess = () => {
          syncKeycloakUser();
          void queryClient.invalidateQueries({
            queryKey: authQueryKeys.currentUserProfile(),
          });
        };

        keycloak.onAuthLogout = () => {
          syncKeycloakUser();
        };
      } finally {
        if (!cancelled) {
          setIsKeycloakReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;

      if (keycloak) {
        keycloak.onAuthSuccess = undefined;
        keycloak.onAuthLogout = undefined;
      }
    };
  }, [queryClient, syncKeycloakUser]);

  const login = useCallback(() => {
    void getKeycloak().login();
  }, []);

  const logout = useCallback(() => {
    void getKeycloak().logout({
      redirectUri: window.location.origin,
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: user !== null,
      isLoading,
      user,
      login,
      logout,
    }),
    [user, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return <KeycloakAuthProvider>{children}</KeycloakAuthProvider>;
}
